import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Webhook,
  Key,
  Plus,
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Trash2,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  Send,
  Copy,
  ExternalLink,
  Info,
  Clock,
  Activity,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from '@/utils/portalNotifications';
import api from '../api/client';
import { DiscoverabilityHint, GrowthCapabilityPanel } from '../components/client/PlanGatingDiscoverability';
import { useIntegrationCapabilities } from '../utils/integrationCapabilityAccess';
import { PortalLoadingPanel, portalPageRoot } from '../components/client/ClientPortalPatterns';
import { cn } from '../lib/utils';
import { buildSafeQueryPath } from '../utils/clientPortalNavigation';

const IntegrationsPage = () => {
  const navigate = useNavigate();
  const {
    canViewWebhooks,
    canWriteWebhooks,
    canViewReadApiKeys,
    canWriteReadApiKeys,
  } = useIntegrationCapabilities();
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testingWebhookId, setTestingWebhookId] = useState(null);
  const [deletingWebhookId, setDeletingWebhookId] = useState(null);
  const [showSecretId, setShowSecretId] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const [readApiKeys, setReadApiKeys] = useState([]);
  const [readApiMeta, setReadApiMeta] = useState(null);
  const [newReadApiSecret, setNewReadApiSecret] = useState(null);
  const [creatingReadKey, setCreatingReadKey] = useState(false);
  const [revokingReadKeyId, setRevokingReadKeyId] = useState(null);
  const [webhookDeliveries, setWebhookDeliveries] = useState([]);
  const [webhookDeliveriesLoading, setWebhookDeliveriesLoading] = useState(false);
  const [webhookDeliveriesFailedOnly, setWebhookDeliveriesFailedOnly] = useState(false);

  const hasWebhooksAccess = canViewWebhooks;

  useEffect(() => {
    if (!hasWebhooksAccess) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [hasWebhooksAccess]);

  useEffect(() => {
    if (!hasWebhooksAccess) {
      setWebhookDeliveries([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setWebhookDeliveriesLoading(true);
      try {
        const d = await api.get('/webhooks/deliveries', {
          params: { limit: 30, failed_only: webhookDeliveriesFailedOnly },
        });
        if (!cancelled) setWebhookDeliveries(d.data?.deliveries || []);
      } catch {
        if (!cancelled) setWebhookDeliveries([]);
      } finally {
        if (!cancelled) setWebhookDeliveriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasWebhooksAccess, webhookDeliveriesFailedOnly]);

  const fetchData = async () => {
    try {
      const [webhooksRes, eventsRes, statsRes] = await Promise.all([
        api.get('/webhooks'),
        api.get('/webhooks/events'),
        api.get('/webhooks/stats')
      ]);
      
      setWebhooks(webhooksRes.data.webhooks || []);
      setRateLimitInfo(webhooksRes.data.rate_limit);
      setAvailableEvents(eventsRes.data.events || []);
      setStats(statsRes.data);
      try {
        const ra = await api.get('/client/integrations/read-api-keys');
        setReadApiKeys(ra.data.keys || []);
        setReadApiMeta({
          data_base_path: ra.data.data_base_path,
          scopes: ra.data.scopes || [],
          auth_headers: ra.data.auth_headers || [],
        });
      } catch (e) {
        setReadApiKeys([]);
        setReadApiMeta(null);
      }
    } catch (error) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const testWebhook = async (webhookId) => {
    setTestingWebhookId(webhookId);
    try {
      const response = await api.post(`/webhooks/${webhookId}/test`);
      if (response.data.success) {
        toast.success(`Test successful! Status: ${response.data.status_code}`);
      } else {
        toast.error(`Test failed: ${response.data.error || 'Unknown error'}`);
      }
      fetchData(); // Refresh to get updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to test webhook');
    } finally {
      setTestingWebhookId(null);
    }
  };

  const toggleWebhook = async (webhookId, currentStatus) => {
    try {
      const endpoint = currentStatus ? 'disable' : 'enable';
      await api.post(`/webhooks/${webhookId}/${endpoint}`);
      toast.success(`Webhook ${currentStatus ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle webhook');
    }
  };

  const deleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;
    
    setDeletingWebhookId(webhookId);
    try {
      await api.delete(`/webhooks/${webhookId}`);
      toast.success('Webhook deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete webhook');
    } finally {
      setDeletingWebhookId(null);
    }
  };

  const regenerateSecret = async (webhookId) => {
    if (!window.confirm('Regenerating the secret will invalidate the current one. Continue?')) return;
    
    try {
      const response = await api.post(`/webhooks/${webhookId}/regenerate-secret`);
      setNewSecret({ id: webhookId, secret: response.data.secret });
      toast.success('Secret regenerated. Copy it now - it won\'t be shown again!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate secret');
    }
  };

  const createReadApiKey = async () => {
    setCreatingReadKey(true);
    try {
      const res = await api.post('/client/integrations/read-api-keys', { name: 'Integration' });
      setNewReadApiSecret(res.data.secret);
      toast.success('Read API key created — copy the secret now.');
      fetchData();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Failed to create read API key');
    } finally {
      setCreatingReadKey(false);
    }
  };

  const revokeReadApiKey = async (keyId) => {
    if (!window.confirm('Revoke this key? Any integration using it will stop working.')) return;
    setRevokingReadKeyId(keyId);
    try {
      await api.delete(`/client/integrations/read-api-keys/${keyId}`);
      toast.success('Read API key revoked');
      setNewReadApiSecret(null);
      fetchData();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Failed to revoke key');
    } finally {
      setRevokingReadKeyId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusBadge = (webhook) => {
    if (!webhook.is_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          <ToggleLeft className="w-3 h-3" />
          Disabled
        </span>
      );
    }
    
    if (webhook.failure_count >= 3) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <AlertTriangle className="w-3 h-3" />
          Degraded
        </span>
      );
    }
    
    if (webhook.last_status && webhook.last_status >= 200 && webhook.last_status < 300) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Healthy
        </span>
      );
    }
    
    if (webhook.last_status) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Error ({webhook.last_status})
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <Clock className="w-3 h-3" />
        Never triggered
      </span>
    );
  };

  if (loading) {
    return (
      <div className={portalPageRoot}>
        <PortalLoadingPanel message="Loading integrations…" />
      </div>
    );
  }

  // Safety net if route guard is bypassed
  if (!hasWebhooksAccess) {
    return (
      <div className={cn(portalPageRoot, 'bg-gray-50')} data-testid="integrations-page">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-midnight-blue flex items-center gap-2">
                  <Webhook className="w-6 h-6 text-electric-teal" />
                  Integrations
                </h1>
                <p className="text-sm text-gray-500">Manage webhooks and external integrations</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto space-y-4 px-4 py-12">
          <DiscoverabilityHint
            title="Webhooks & external integrations"
            body="Connect compliance events to your own endpoints with signed payloads and retries — designed for larger, delegated operations (Professional tier). Your current workflows are unchanged until you enable this in Billing."
            onCta={() => navigate(buildSafeQueryPath('/settings/billing', { upgrade_to: 'PLAN_3_PRO' }))}
            dataTestId="integrations-discoverability-hint"
          />

          <GrowthCapabilityPanel title="Overview of integration capabilities (optional read)">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="webhooks-preview">
              <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
                <Webhook className="mt-0.5 h-5 w-5 text-electric-teal" />
                <div>
                  <p className="font-medium text-gray-900">Custom webhooks</p>
                  <p className="text-sm text-gray-500">Send events to your own endpoints</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
                <Activity className="mt-0.5 h-5 w-5 text-electric-teal" />
                <div>
                  <p className="font-medium text-gray-900">Real-time events</p>
                  <p className="text-sm text-gray-500">Notifications when compliance-relevant activity occurs</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
                <Shield className="mt-0.5 h-5 w-5 text-electric-teal" />
                <div>
                  <p className="font-medium text-gray-900">Signed payloads</p>
                  <p className="text-sm text-gray-500">HMAC-SHA256 verification</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
                <RefreshCw className="mt-0.5 h-5 w-5 text-electric-teal" />
                <div>
                  <p className="font-medium text-gray-900">Automatic retries</p>
                  <p className="text-sm text-gray-500">Delivery with backoff</p>
                </div>
              </div>
            </div>
          </GrowthCapabilityPanel>
        </main>
      </div>
    );
  }

  return (
    <div className={cn(portalPageRoot, 'bg-gray-50')} data-testid="integrations-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-midnight-blue flex items-center gap-2">
                  <Webhook className="w-6 h-6 text-electric-teal" />
                  Integrations
                </h1>
                <p className="text-sm text-gray-500">Manage webhooks and external integrations</p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-electric-teal hover:bg-teal-600 min-h-11 w-full sm:w-auto shrink-0"
              data-testid="create-webhook-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Webhook
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-8" data-testid="webhook-stats">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Webhooks</p>
                    <p className="text-2xl font-bold text-midnight-blue">{stats.total_webhooks}</p>
                  </div>
                  <Webhook className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active</p>
                    <p className="text-2xl font-bold text-green-600">{stats.active_webhooks}</p>
                  </div>
                  <Activity className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Deliveries</p>
                    <p className="text-2xl font-bold text-midnight-blue">{stats.total_deliveries}</p>
                  </div>
                  <Send className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Success Rate</p>
                    <p className="text-2xl font-bold text-electric-teal">
                      {stats.success_rate?.toFixed(1) || 0}%
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-electric-teal" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {hasWebhooksAccess && (
          <Card className="mb-8" data-testid="webhook-deliveries-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2 text-midnight-blue">
                    <Send className="w-5 h-5 text-electric-teal" />
                    Recent webhook deliveries
                  </CardTitle>
                  <CardDescription>
                    Outbound HTTP attempts logged for your tenant (URL, event alias, status). Toggle to show failures
                    only.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Failures only</span>
                  <Switch
                    checked={webhookDeliveriesFailedOnly}
                    onCheckedChange={setWebhookDeliveriesFailedOnly}
                    data-testid="webhook-deliveries-failed-only"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {webhookDeliveriesLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading deliveries…
                </div>
              ) : webhookDeliveries.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">No delivery rows in this view yet.</p>
              ) : (
                <div className="overflow-x-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-gray-600">
                        <th className="p-2 font-medium">Time</th>
                        <th className="p-2 font-medium">Status</th>
                        <th className="p-2 font-medium">Event</th>
                        <th className="p-2 font-medium">Target</th>
                        <th className="p-2 font-medium">HTTP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {webhookDeliveries.map((row, idx) => (
                        <tr key={`${row.created_at || ''}-${idx}`} className="border-t border-gray-100">
                          <td className="p-2 text-gray-700 whitespace-nowrap">
                            {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                          </td>
                          <td className="p-2">
                            <span
                              className={
                                row.status === 'failed' || row.status === 'error'
                                  ? 'text-red-600'
                                  : 'text-green-700'
                              }
                            >
                              {row.status || '—'}
                            </span>
                          </td>
                          <td className="p-2 font-mono text-xs max-w-[140px] truncate" title={row.event_alias}>
                            {row.event_alias || '—'}
                          </td>
                          <td className="p-2 font-mono text-xs max-w-[200px] truncate" title={row.target_url}>
                            {row.target_url || '—'}
                          </td>
                          <td className="p-2 text-gray-600">{row.response_code ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rate Limit & Retry Policy Info */}
        {rateLimitInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8" data-testid="rate-limit-info">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Webhook Delivery Policy</p>
                <ul className="space-y-1 text-blue-700">
                  <li>• Rate limit: {rateLimitInfo.max_requests_per_minute} requests/minute per client</li>
                  <li>• Retries: {rateLimitInfo.max_retries} attempts with {rateLimitInfo.retry_backoff} backoff</li>
                  <li>• Timeout: {rateLimitInfo.timeout_seconds} seconds per request</li>
                  <li>• Auto-disable: After {rateLimitInfo.auto_disable_after_failures} consecutive failures</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {readApiMeta && hasWebhooksAccess && (
          <Card className="mb-8" data-testid="read-api-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-midnight-blue">
                <Key className="w-5 h-5 text-electric-teal" />
                Read API (HTTP)
              </CardTitle>
              <CardDescription>
                Pull properties, requirements, priorities, and compliance score with a scoped key. Same plan gate as
                webhooks (Professional). Discovery document:{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">GET /api/client-data/v1/capabilities</code>. Use{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">Authorization: Bearer &lt;token&gt;</code> or{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">X-Pleerity-Read-Key</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-800 mb-1">Base path</p>
                <code className="block text-xs bg-gray-100 p-2 rounded break-all">
                  {readApiMeta.data_base_path || '/api/client-data/v1'}
                </code>
                <p className="mt-2 text-xs text-gray-500">
                  Example: GET {readApiMeta.data_base_path || '/api/client-data/v1'}/properties
                </p>
              </div>
              {readApiMeta.scopes?.length > 0 && (
                <div className="text-sm">
                  <p className="font-medium text-gray-800 mb-1">Scopes</p>
                  <div className="flex flex-wrap gap-1">
                    {readApiMeta.scopes.map((s) => (
                      <span
                        key={s}
                        className="text-xs px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-gray-700"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {newReadApiSecret && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="font-medium text-amber-900 mb-2">New secret (copy now)</p>
                  <code className="block text-xs break-all bg-white p-2 rounded border mb-2">{newReadApiSecret}</code>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(newReadApiSecret)}>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy secret
                  </Button>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  onClick={createReadApiKey}
                  disabled={creatingReadKey}
                  className="bg-midnight-blue hover:bg-midnight-blue/90"
                  data-testid="create-read-api-key-btn"
                >
                  {creatingReadKey ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                  Create read API key
                </Button>
              </div>
              {readApiKeys.length > 0 && (
                <ul className="divide-y border rounded-lg">
                  {readApiKeys.map((k) => (
                    <li key={k.key_id} className="flex items-center justify-between p-3 text-sm">
                      <div>
                        <p className="font-medium text-gray-900">{k.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{k.key_hint}</p>
                        <p className="text-xs text-gray-400">Created {k.created_at}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        disabled={revokingReadKeyId === k.key_id}
                        onClick={() => revokeReadApiKey(k.key_id)}
                      >
                        {revokingReadKeyId === k.key_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Revoke
                          </>
                        )}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {/* Webhooks List */}
        <div className="space-y-4">
          {webhooks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Webhook className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No webhooks configured</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Create a webhook to receive real-time notifications when compliance events occur.
                </p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-electric-teal hover:bg-teal-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Webhook
                </Button>
              </CardContent>
            </Card>
          ) : (
            webhooks.map((webhook) => (
              <Card key={webhook.webhook_id} className="overflow-hidden" data-testid={`webhook-${webhook.webhook_id}`}>
                <CardContent className="p-0">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-midnight-blue">{webhook.name}</h3>
                          {getStatusBadge(webhook)}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <ExternalLink className="w-3 h-3" />
                          <span className="font-mono text-xs break-all">{webhook.url}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={webhook.is_active}
                          onCheckedChange={() => toggleWebhook(webhook.webhook_id, webhook.is_active)}
                          data-testid={`toggle-${webhook.webhook_id}`}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Event Types */}
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-2">Subscribed Events</p>
                    <div className="flex flex-wrap gap-2">
                      {webhook.event_types?.map((event) => (
                        <span 
                          key={event}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white border border-gray-200 text-gray-700"
                        >
                          {event}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Delivery Stats */}
                  <div className="px-4 py-3 grid grid-cols-4 gap-4 text-sm border-b border-gray-100">
                    <div>
                      <p className="text-gray-500">Last Status</p>
                      <p className="font-medium">
                        {webhook.last_status ? (
                          <span className={webhook.last_status >= 200 && webhook.last_status < 300 ? 'text-green-600' : 'text-red-600'}>
                            HTTP {webhook.last_status}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Triggered</p>
                      <p className="font-medium text-midnight-blue">
                        {webhook.last_triggered ? (
                          new Date(webhook.last_triggered).toLocaleString()
                        ) : (
                          <span className="text-gray-400">Never</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Success Rate</p>
                      <p className="font-medium text-midnight-blue">
                        {webhook.success_rate !== null ? `${webhook.success_rate}%` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Failures</p>
                      <p className={`font-medium ${webhook.failure_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {webhook.failure_count}
                      </p>
                    </div>
                  </div>
                  
                  {/* Last Error (if any) */}
                  {webhook.last_error && (
                    <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                      <p className="text-xs text-red-700">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {webhook.last_error}
                      </p>
                    </div>
                  )}
                  
                  {/* New Secret Display */}
                  {newSecret?.id === webhook.webhook_id && (
                    <div className="px-4 py-3 bg-green-50 border-b border-green-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-green-800 mb-1">New Signing Secret (copy now!)</p>
                          <code className="text-sm font-mono text-green-700 break-all">{newSecret.secret}</code>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(newSecret.secret)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="px-4 py-3 flex items-center justify-between bg-white">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => testWebhook(webhook.webhook_id)}
                        disabled={testingWebhookId === webhook.webhook_id}
                        data-testid={`test-${webhook.webhook_id}`}
                      >
                        {testingWebhookId === webhook.webhook_id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Send className="w-4 h-4 mr-1" />
                        )}
                        Test
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => regenerateSecret(webhook.webhook_id)}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Regenerate Secret
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteWebhook(webhook.webhook_id)}
                      disabled={deletingWebhookId === webhook.webhook_id}
                      data-testid={`delete-${webhook.webhook_id}`}
                    >
                      {deletingWebhookId === webhook.webhook_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Available Events Reference */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-electric-teal" />
                Available Event Types
              </CardTitle>
              <CardDescription>
                Subscribe to these events when creating a webhook
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-gray-100">
                {availableEvents.map((event) => (
                  <div key={event.type} className="py-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-midnight-blue">{event.name}</p>
                        <code className="text-xs text-gray-500 font-mono">{event.type}</code>
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                      </div>
                    </div>
                    {event.payload_fields && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {event.payload_fields.map((field) => (
                          <span 
                            key={field}
                            className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded font-mono"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Create Webhook Modal */}
      {showCreateModal && (
        <CreateWebhookModal
          availableEvents={availableEvents}
          onClose={() => setShowCreateModal(false)}
          onCreated={(secret) => {
            setShowCreateModal(false);
            fetchData();
            if (secret) {
              toast.success('Webhook created! Copy the signing secret.', { duration: 5000 });
            }
          }}
        />
      )}
    </div>
  );
};

// Create Webhook Modal Component
const CreateWebhookModal = ({ availableEvents, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [customSecret, setCustomSecret] = useState('');
  const [useCustomSecret, setUseCustomSecret] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdSecret, setCreatedSecret] = useState(null);

  const toggleEvent = (eventType) => {
    setSelectedEvents(prev => 
      prev.includes(eventType) 
        ? prev.filter(e => e !== eventType)
        : [...prev, eventType]
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a webhook name');
      return;
    }
    if (!url.trim() || !url.startsWith('http')) {
      toast.error('Please enter a valid URL');
      return;
    }
    if (selectedEvents.length === 0) {
      toast.error('Please select at least one event type');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/webhooks', {
        name: name.trim(),
        url: url.trim(),
        event_types: selectedEvents,
        secret: useCustomSecret && customSecret.trim() ? customSecret.trim() : null
      });
      
      setCreatedSecret(response.data.secret);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
      setCreating(false);
    }
  };

  const copyAndClose = () => {
    if (createdSecret) {
      navigator.clipboard.writeText(createdSecret);
      toast.success('Secret copied to clipboard');
    }
    onCreated(createdSecret);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="create-webhook-modal">
        {!createdSecret ? (
          <>
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-midnight-blue">Create Webhook</h2>
              <p className="text-sm text-gray-500 mt-1">
                Configure a new webhook endpoint to receive compliance events
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Integration Webhook"
                  data-testid="webhook-name-input"
                />
              </div>
              
              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL</label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://api.example.com/webhook"
                  data-testid="webhook-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be a valid HTTPS URL that can receive POST requests
                </p>
              </div>
              
              {/* Custom Secret Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm text-gray-700">Use Custom Secret</p>
                  <p className="text-xs text-gray-500">Or we'll generate a secure one for you</p>
                </div>
                <Switch
                  checked={useCustomSecret}
                  onCheckedChange={setUseCustomSecret}
                />
              </div>
              
              {useCustomSecret && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Signing Secret</label>
                  <Input
                    type="password"
                    value={customSecret}
                    onChange={(e) => setCustomSecret(e.target.value)}
                    placeholder="Enter your secret..."
                    data-testid="webhook-secret-input"
                  />
                </div>
              )}
              
              {/* Event Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Types</label>
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {availableEvents.map((event) => (
                    <label 
                      key={event.type}
                      className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(event.type)}
                        onChange={() => toggleEvent(event.type)}
                        className="mt-1 rounded border-gray-300 text-electric-teal focus:ring-electric-teal"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{event.name}</p>
                        <p className="text-xs text-gray-500">{event.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-electric-teal hover:bg-teal-600"
                data-testid="submit-webhook-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Webhook
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-midnight-blue">Webhook Created!</h2>
                  <p className="text-sm text-gray-500">Save your signing secret</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <span>
                    <strong>Important:</strong> Copy this signing secret now. It will only be shown once and cannot be retrieved later.
                  </span>
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Signing Secret</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-white p-3 rounded border border-gray-200 break-all">
                    {createdSecret}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdSecret);
                      toast.success('Copied!');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>How to verify webhooks:</strong><br />
                  We sign all webhook payloads using HMAC-SHA256. The signature is included in the 
                  <code className="mx-1 px-1 bg-blue-100 rounded">X-Webhook-Signature</code> header 
                  in the format <code className="mx-1 px-1 bg-blue-100 rounded">sha256={"<hex_digest>"}</code>.
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 flex justify-end">
              <Button
                onClick={copyAndClose}
                className="bg-electric-teal hover:bg-teal-600"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Secret & Close
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IntegrationsPage;
